import cookie from 'js/lib/cookie';

import type { EmbeddableItemTypes } from 'bundles/ai-authoring';
import { CourseCatalogType } from 'bundles/author-course/utils/types';
import type { AuthoringCourseContextsWithCreationContexts } from 'bundles/authoring/common/types/authoringCourseContexts';
import sessionStorageEx from 'bundles/common/utils/sessionStorageEx';

import _t from 'i18n!nls/teach-course';

const COURSE_BUILDER_ORIGIN_PATH_KEY = 'courseBuilderOriginPath';

const COURSE_BUILDER_PILOT_VIEW_KEY = 'hasCourseBuilderPilotView';

export const setHasSimplifiedViewCookie = (isSimplified: boolean) => {
  cookie.set(COURSE_BUILDER_PILOT_VIEW_KEY, isSimplified, { days: 365 });
};

export const isCourseBuilderEnabled = ({ courseCatalogType }: { courseCatalogType?: CourseCatalogType | null }) => {
  return CourseCatalogType.ENTERPRISE === courseCatalogType;
};

export const hasSimplifiedViewSupport = ({
  courseCatalogType,
  contexts,
}: {
  courseCatalogType?: CourseCatalogType | null;
  contexts?: Array<AuthoringCourseContextsWithCreationContexts>;
}) => {
  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  if (!contexts?.length || contexts.length > 1 || contexts?.[0].typeName !== 'publicBranchContext') {
    return false;
  }
  return isCourseBuilderEnabled({ courseCatalogType });
};

export const hasSimplifiedView = ({
  courseCatalogType,
  contexts,
}: {
  courseCatalogType?: CourseCatalogType | null;
  contexts?: Array<AuthoringCourseContextsWithCreationContexts>;
}) => {
  if (!hasSimplifiedViewSupport({ courseCatalogType, contexts })) {
    return false;
  }
  const simplifiedViewCookie = cookie.get(COURSE_BUILDER_PILOT_VIEW_KEY);

  return simplifiedViewCookie === 'true' || simplifiedViewCookie == null;
};

export const getCustomCoursesLink = (thirdPartyOrganizationSlug?: string) =>
  `/o/${thirdPartyOrganizationSlug}/admin/custom-courses`;

export const setCourseBuilderPathnameReferrer = (value: string) => {
  sessionStorageEx.setItem(COURSE_BUILDER_ORIGIN_PATH_KEY, value, JSON.stringify);
};

export const getCourseBuilderPathnameReferrer = (thirdPartyOrganizationSlug?: string, adminSlug?: string) => {
  const originPath = sessionStorageEx.getItem(COURSE_BUILDER_ORIGIN_PATH_KEY, JSON.parse, '');

  // In case the author is admin for multiple institutions.
  const isCurrentOrg = originPath.includes(thirdPartyOrganizationSlug) || originPath.includes(adminSlug);

  if (isCurrentOrg || originPath.includes('admin/courses')) {
    return originPath;
  }

  return getCustomCoursesLink(thirdPartyOrganizationSlug);
};

export const getCourseBuilderItemType = (typeName: string): EmbeddableItemTypes | null => {
  switch (typeName) {
    case 'supplement':
      return 'READING';
    case 'lecture':
      return 'LECTURE';
    case 'ungradedWidget':
      return 'WIDGET';
    case 'discussionPrompt':
      return 'DISCUSSION_PROMPT';
    case 'peer':
    case 'closedPeer':
    case 'gradedPeer':
    case 'phasedPeer':
      return 'PEER_REVIEW';
    case 'quiz':
    case 'exam':
      return 'QUIZ';
    case 'staffGraded':
    case 'ungradedAssignment':
      return 'ASSIGNMENT';
    case 'ungradedLab':
      return 'UNGRADED_LAB';
    case 'programming':
    case 'gradedProgramming':
    case 'ungradedProgramming':
      return 'PROGRAMMING_ASSIGNMENT';
    default:
      return null;
  }
};

export const itemTypeSupportsRefinement = (typeName: string) => {
  return ['supplement', 'lecture'].includes(typeName);
};

// one-off cases to allow enterprise orgs mapped to multiple authoring partners to work
export const COURSE_BUILDER_PARTNER_EXCLUSIONS = new Set([
  '227', // National Arts Strategies
  '451', // Unicef
]);

export const getCourseBuilderItemTypeLabel = (
  itemType: string
): { getPrefixTranslation: () => string; typeName: string } => {
  switch (itemType) {
    case 'READING':
      return { getPrefixTranslation: () => _t('Reading'), typeName: 'supplement' };
    case 'LECTURE':
      return { getPrefixTranslation: () => _t('Video'), typeName: 'lecture' };
    case 'WIDGET':
      return { getPrefixTranslation: () => _t('Ungraded plugin'), typeName: 'ungradedWidget' };
    case 'DISCUSSION_PROMPT':
      return { getPrefixTranslation: () => _t('Discussion prompt'), typeName: 'discussionPrompt' };
    case 'PEER_REVIEW':
      return { getPrefixTranslation: () => _t('Peer review'), typeName: 'peer' };
    case 'QUIZ':
      return { getPrefixTranslation: () => _t('Quiz'), typeName: 'quiz' };
    case 'ASSIGNMENT':
      return { getPrefixTranslation: () => _t('Assignment'), typeName: 'staffGraded' };
    case 'PROGRAMMING_ASSIGNMENT':
      // technically programming assignments may be graded or ungraded but we dont seem to use this typeName so it should be ok.
      return { getPrefixTranslation: () => _t('Programming Assignment'), typeName: 'ungradedProgramming' };
    case 'UNGRADED_LAB':
      return { getPrefixTranslation: () => _t('Ungraded lab'), typeName: 'ungradedLab' };
    case 'LTI':
      return { getPrefixTranslation: () => _t('App item'), typeName: 'ungradedLti' };
    case 'TEAMMATE_REVIEW':
      return { getPrefixTranslation: () => _t('Teammate review'), typeName: 'teammateReview' };
    default:
      return { getPrefixTranslation: () => '', typeName: '' };
  }
};
